import { Trans } from "@lingui/react/macro";
import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { LimeModal } from "@/Components/NextBase/LimeModal";
import { LimeLoader } from "@/Components/NextBase/LimeLoader";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import { api } from "@/lib/api-client";
import { addToast } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { useForm } from "@mantine/form";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

type DeleteFreezeFormValues = {
  restoreAppointments: boolean;
  extendValidity: boolean;
};

export function CustomerSubscriptionDeleteFreezeModal({
  isOpen,
  onClose,
  customerSubscriptionId,
  scheduledFreezeId,
}: {
  isOpen: boolean;
  onClose: () => void;
  customerSubscriptionId: number | null;
  scheduledFreezeId: number | null;
}) {
  const { data: freezeData, isPending: isLoadingFreeze } =
    api.customer.useGetScheduledSubscriptionFreeze({
      customerSubscriptionId,
      scheduledFreezeId,
    });

  const isFreezeCurrentlyActive =
    freezeData != null &&
    dayjs().isBetween(
      freezeData.scheduledFreeze.freezeStart,
      freezeData.scheduledFreeze.freezeEnd,
      null,
      "[]",
    );

  const form = useForm<DeleteFreezeFormValues>({
    initialValues: {
      restoreAppointments: true,
      extendValidity: true,
    },
  });
  const {
    mutateAsync: deleteScheduledFreeze,
    isPending: isDeleting,
    processedErrorMessage,
  } = api.customer.useDeleteScheduledSubscriptionFreeze();

  const handleConfirm = async (values: DeleteFreezeFormValues) => {
    if (customerSubscriptionId == null || scheduledFreezeId == null) return;
    try {
      await deleteScheduledFreeze({
        customerSubscriptionId,
        scheduledFreezeId,
        query: {
          restoreAppointments: values.restoreAppointments,
          extendValidity: values.extendValidity,
        },
      });
      addToast({
        title: t`Načrtovana zamrznitev izbrisana`,
        description: t`Načrtovana zamrznitev je bila uspešno izbrisana`,
        color: "success",
      });
      onClose();
    } catch (e) {
      addToast({
        title: t`Napaka`,
        description: processedErrorMessage,
        color: "danger",
      });
    }
  };

  return (
    <LimeModal
      isOpen={isOpen}
      onClose={onClose}
      data-identifier="delete-freeze-modal"
    >
      <ModalContent>
        <ModalHeader>
          <Trans>Izbriši načrtovano zamrznitev</Trans>
        </ModalHeader>
        <form onSubmit={form.onSubmit(handleConfirm)}>
          <ModalBody>
            {isLoadingFreeze ? (
              <LimeLoader />
            ) : (
              <>
                <LimeSwitch
                  isCompact={true}
                  size="sm"
                  title={t`Ob izbrisu zamrznitve povrni izbrisane termine`}
                  {...form.getInputProps("restoreAppointments", {
                    type: "checkbox",
                  })}
                />
                {isFreezeCurrentlyActive && (
                  <LimeSwitch
                    isCompact={true}
                    size="sm"
                    title={t`Podaljšaj veljavnost paketa za število doslej zamrznjenih dni`}
                    {...form.getInputProps("extendValidity", {
                      type: "checkbox",
                    })}
                  />
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onClose} isDisabled={isDeleting}>
              <Trans>Prekliči</Trans>
            </Button>
            <Button
              data-identifier="delete-freeze-confirm-btn"
              color="danger"
              variant="solid"
              type="submit"
              isLoading={isDeleting}
            >
              <Trans>Izbriši</Trans>
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </LimeModal>
  );
}
